import React, { createContext, useState, useEffect, useContext } from 'react';
import { 
  CognitoUserPool, 
  CognitoUserAttribute, 
  CognitoUser, 
  AuthenticationDetails 
} from 'amazon-cognito-identity-js';

// Create the Auth Context
const AuthContext = createContext(null);

// AWS Cognito configuration
const poolData = {
  UserPoolId: 'eu-west-3_R1BtCNr0R',
  ClientId: '27sqasv8u9b6l0iba65buvse59',
  region: 'eu-west-3',
  storage: sessionStorage // Use sessionStorage for session persistence
};

// GetUser error codes that mean Cognito revoked the session: user disabled,
// deleted, signed out everywhere, or an admin reset the password. Matches
// aws-amplify's isSessionInvalid classification — callers fail closed on these.
const isSessionRevoked = (err) =>
  err.code === 'NotAuthorizedException' ||
  err.code === 'UserNotFoundException' ||
  err.code === 'PasswordResetRequiredException';

// Custom hook to use the Auth Context
export function useAuth() {
  return useContext(AuthContext);
}

// Auth Provider component
export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [userGroups, setUserGroups] = useState([]);
  const [organization, setOrganization] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState(null);
  
  // NEW: Add state for handling password change challenge
  const [newPasswordRequired, setNewPasswordRequired] = useState(false);
  const [currentCognitoUser, setCurrentCognitoUser] = useState(null);

  // NEW: Add state for forgot password flow
  const [forgotPasswordMode, setForgotPasswordMode] = useState(false);
  const [forgotPasswordUser, setForgotPasswordUser] = useState(null);
  const [forgotPasswordCode, setForgotPasswordCode] = useState('');
  const [forgotPasswordError, setForgotPasswordError] = useState('');
  
  // Create the user pool
  const userPool = new CognitoUserPool(poolData);

  // Configure session duration (8 hours = 28800 seconds)
  const sessionDuration = 28800; // 8 hours in seconds

  // Function to parse JWT token and extract claims
  const parseJwt = (token) => {
    try {
      return JSON.parse(atob(token.split('.')[1]));
    } catch (e) {
      return null;
    }
  };

  // Function to extract user groups from Cognito token
  const extractUserGroups = (idToken) => {
    const decoded = parseJwt(idToken);
    if (decoded && decoded['cognito:groups']) {
      return decoded['cognito:groups'];
    }
    return [];
  };

  // Function to determine user's organization
  const determineOrganization = (groups) => {
    // Filter out access control groups
    const accessControlGroups = ['Admins', 'full_access'];
    const orgGroups = groups.filter(group => !accessControlGroups.includes(group));
    
    // Return the first organization (in your case, it would be 'msn')
    return orgGroups.length > 0 ? orgGroups[0] : null;
  };

  // Check if user is admin
  const isAdmin = () => {
    return userGroups.includes('Admins');
  };

  // Check if user has access to specific section
  const hasAccess = (section) => {
    if (userGroups.includes('Admins') || userGroups.includes('full_access')) {
      return true;
    }
    
    // Check for specific access
    return userGroups.includes(`${section}_access`);
  };

  // Local inspection mode (`npm run dev:local`): serve a stub msn user so the
  // management pages render without Cognito. Development builds only — CRA inlines
  // NODE_ENV=production in real builds, so this whole branch is compiled out there.
  const devBypass = process.env.NODE_ENV === 'development'
    && process.env.REACT_APP_DEV_BYPASS_AUTH === 'true';

  // Effect to check if user is already signed in
  useEffect(() => {
    const checkAuthState = async () => {
      if (devBypass) {
        console.warn('⚠️ REACT_APP_DEV_BYPASS_AUTH actief — lokale inspectie zonder login (alleen dev-build)');
        setCurrentUser({ username: 'dev-bypass', email: 'dev@localhost' });
        setUserGroups(['full_access', 'msn']);
        setOrganization('msn');
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        const cognitoUser = userPool.getCurrentUser();
        
        if (cognitoUser) {
          cognitoUser.getSession((err, session) => {
            if (err) {
              console.error("Session error:", err);
              setLoading(false);
              return;
            }
            
            if (session && session.isValid()) {
              // Get the id token
              const idToken = session.getIdToken().getJwtToken();
              const groups = extractUserGroups(idToken);
              const org = determineOrganization(groups);
              
              // Store tokens in session storage
              sessionStorage.setItem('idToken', idToken);
              sessionStorage.setItem('accessToken', session.getAccessToken().getJwtToken());
              
              // Get user attributes
              cognitoUser.getUserAttributes((err, attributes) => {
                if (err) {
                  console.error("Error getting user attributes:", err);
                  // getSession only checks token expiry locally, so this
                  // GetUser call is the load path's only server-side check —
                  // fail closed when the session was revoked
                  if (isSessionRevoked(err)) {
                    cognitoUser.signOut();
                    sessionStorage.removeItem('idToken');
                    sessionStorage.removeItem('accessToken');
                    setLoading(false);
                    return;
                  }
                }

                const userData = {};
                (attributes || []).forEach(attr => {
                  userData[attr.Name] = attr.Value;
                });

                // Attributes are enrichment, not a precondition: on a
                // transient failure, fall back to the id-token claims so the
                // valid session still authenticates
                if (err) {
                  const claims = parseJwt(idToken) || {};
                  ['email', 'name', 'given_name', 'family_name'].forEach(claim => {
                    if (claims[claim]) userData[claim] = claims[claim];
                  });
                }

                setCurrentUser({
                  ...userData,
                  username: cognitoUser.username
                });
                setUserGroups(groups);
                setOrganization(org);
                setLoading(false);
              });
            } else {
              setLoading(false);
            }
          });
        } else {
          setLoading(false);
        }
      } catch (error) {
        console.error("Error checking auth state:", error);
        setLoading(false);
      }
    };
    
    checkAuthState();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  // Function to complete new password setup
  const completeNewPassword = (newPassword) => {
    return new Promise((resolve, reject) => {
      if (!currentCognitoUser) {
        setAuthError('No user in new password flow');
        return reject(new Error('No user in new password flow'));
      }

      currentCognitoUser.completeNewPasswordChallenge(newPassword, {}, {
        onSuccess: (result) => {
          console.log('Password change successful');

          // Get the tokens
          const idToken = result.getIdToken().getJwtToken();
          const accessToken = result.getAccessToken().getJwtToken();

          // Extract groups and organization
          const groups = extractUserGroups(idToken);
          const org = determineOrganization(groups);

          // Store tokens in session storage
          sessionStorage.setItem('idToken', idToken);
          sessionStorage.setItem('accessToken', accessToken);
          sessionStorage.setItem('sessionStartTime', Date.now().toString());

          // Get user attributes
          currentCognitoUser.getUserAttributes((err, attributes) => {
            if (err) {
              console.error("Error getting user attributes:", err);
              // The challenge already succeeded server-side (the new password
              // is active and the session is cached), so a bare reject strands
              // the UI on the password form replaying a consumed challenge
              // session. These codes mean Cognito revoked the fresh session —
              // fail closed all the way back to the login form: currentUser
              // still holds the temp challenge user, and clearing only the
              // challenge flags would redirect a token-less user into the app
              if (isSessionRevoked(err)) {
                currentCognitoUser.signOut();
                sessionStorage.removeItem('idToken');
                sessionStorage.removeItem('accessToken');
                setCurrentUser(null);
                setUserGroups([]);
                setOrganization(null);
                setNewPasswordRequired(false);
                setCurrentCognitoUser(null);
                setAuthError(err.message || 'Failed to set new password');
                return reject(err);
              }
            }

            const userData = {};
            (attributes || []).forEach(attr => {
              userData[attr.Name] = attr.Value;
            });

            // Attributes are enrichment, not a precondition: on a transient
            // failure, fall back to the id-token claims so the completed
            // password change still signs the user in
            if (err) {
              const claims = parseJwt(idToken) || {};
              ['email', 'name', 'given_name', 'family_name'].forEach(claim => {
                if (claims[claim]) userData[claim] = claims[claim];
              });
            }

            // Update state
            setCurrentUser({
              ...userData,
              username: currentCognitoUser.username
            });
            setUserGroups(groups);
            setOrganization(org);
            setNewPasswordRequired(false);
            setCurrentCognitoUser(null);

            resolve({
              user: {
                ...userData,
                username: currentCognitoUser.username
              },
              groups,
              organization: org
            });
          });
        },
        onFailure: (err) => {
          console.error("Password change failure:", err);
          // completeNewPasswordChallenge replays currentCognitoUser.Session and
          // the SDK never refreshes it on failure, so an expired or consumed
          // challenge session fails identically on every retry. Fail closed
          // back to the login form; a policy error (InvalidPasswordException)
          // is retryable and must keep the challenge form
          if (isSessionRevoked(err)) {
            setCurrentUser(null);
            setUserGroups([]);
            setOrganization(null);
            setNewPasswordRequired(false);
            setCurrentCognitoUser(null);
          }
          setAuthError(err.message || 'Failed to set new password');
          reject(err);
        }
      });
    });
  };

  // Function to initiate forgot password flow
  const initiateForgotPassword = (username) => {
    return new Promise((resolve, reject) => {
      setForgotPasswordError('');

      const cognitoUser = new CognitoUser({
        Username: username,
        Pool: userPool,
      });

      cognitoUser.forgotPassword({
        onSuccess: (data) => {
          console.log('Forgot password code sent successfully');
          setForgotPasswordUser(cognitoUser);
          setForgotPasswordMode(true);
          resolve(data);
        },
        onFailure: (err) => {
          console.error("Forgot password initiation failure:", err);
          setForgotPasswordError(err.message || 'Failed to send verification code');
          reject(err);
        }
      });
    });
  };

  // Function to confirm forgot password with verification code
  const confirmForgotPassword = (newPassword, code) => {
    return new Promise((resolve, reject) => {
      if (!forgotPasswordUser) {
        setForgotPasswordError('No user in forgot password flow');
        return reject(new Error('No user in forgot password flow'));
      }

      forgotPasswordUser.confirmPassword(code, newPassword, {
        onSuccess: () => {
          console.log('Password reset successful');
          setForgotPasswordMode(false);
          setForgotPasswordUser(null);
          setForgotPasswordCode('');
          resolve();
        },
        onFailure: (err) => {
          console.error("Password reset confirmation failure:", err);
          setForgotPasswordError(err.message || 'Failed to reset password');
          reject(err);
        }
      });
    });
  };

  // Leave the forgot-password flow without completing it. forgotPasswordMode
  // lives here, so LoginPage cannot get the user back to the login form on its
  // own — its "Terug" button needs this to clear the mode, the pending user
  // and the last error.
  const cancelForgotPassword = () => {
    setForgotPasswordMode(false);
    setForgotPasswordUser(null);
    setForgotPasswordCode('');
    setForgotPasswordError('');
  };
  
  // Login function
  const login = (username, password) => {
    return new Promise((resolve, reject) => {
      setAuthError(null);
      
      const authenticationDetails = new AuthenticationDetails({
        Username: username,
        Password: password,
      });
      
      const cognitoUser = new CognitoUser({
        Username: username,
        Pool: userPool,
      });
      
      // Set the session validity duration (in seconds)
      cognitoUser.setAuthenticationFlowType('USER_SRP_AUTH');
      
      cognitoUser.authenticateUser(authenticationDetails, {
        onSuccess: (result) => {
          // Get the tokens
          const idToken = result.getIdToken().getJwtToken();
          const accessToken = result.getAccessToken().getJwtToken();
          
          // Extract groups and organization
          const groups = extractUserGroups(idToken);
          const org = determineOrganization(groups);
          
          // Store tokens in session storage
          sessionStorage.setItem('idToken', idToken);
          sessionStorage.setItem('accessToken', accessToken);
          sessionStorage.setItem('sessionStartTime', Date.now().toString());
          
          // Get user attributes
          cognitoUser.getUserAttributes((err, attributes) => {
            if (err) {
              console.error("Error getting user attributes:", err);
              // The SDK cached this session before onSuccess fired, so a bare
              // reject would leave the user authenticated behind a dead login
              // form. These codes mean Cognito revoked the fresh session
              // (user disabled or deleted) — fail closed
              if (isSessionRevoked(err)) {
                cognitoUser.signOut();
                sessionStorage.removeItem('idToken');
                sessionStorage.removeItem('accessToken');
                setAuthError(err.message || 'Failed to login');
                return reject(err);
              }
            }

            const userData = {};
            (attributes || []).forEach(attr => {
              userData[attr.Name] = attr.Value;
            });

            // Attributes are enrichment, not a precondition: on a transient
            // failure, fall back to the id-token claims so the fresh session
            // still logs the user in
            if (err) {
              const claims = parseJwt(idToken) || {};
              ['email', 'name', 'given_name', 'family_name'].forEach(claim => {
                if (claims[claim]) userData[claim] = claims[claim];
              });
            }

            // Update state
            setCurrentUser({
              ...userData,
              username: cognitoUser.username
            });
            setUserGroups(groups);
            setOrganization(org);
            
            resolve({
              user: {
                ...userData,
                username: cognitoUser.username
              },
              groups,
              organization: org
            });
          });
        },
        onFailure: (err) => {
          console.error("Authentication failure:", err);
          setAuthError(err.message || 'Failed to login');
          reject(err);
        },
        // NEW: Add handler for NEW_PASSWORD_REQUIRED challenge
        newPasswordRequired: (userAttributes, requiredAttributes) => {
          console.log('New password required challenge received');
          // Save the current Cognito user to complete the auth flow later
          setCurrentCognitoUser(cognitoUser);
          
          // Set state indicating new password is required
          setNewPasswordRequired(true);
          
          // Return a temporary user object for the UI to show password change screen
          const tempUser = {
            username,
            needsNewPassword: true
          };
          
          setCurrentUser(tempUser);
          
          // Resolve with temp user and flag indicating password change needed
          resolve({
            user: tempUser,
            newPasswordRequired: true
          });
        }
      });
    });
  };
  
  // Logout function
  const logout = () => {
    const cognitoUser = userPool.getCurrentUser();
    if (cognitoUser) {
      cognitoUser.signOut();
    }
    // Clear state even without a pool user: mid-challenge (temp password
    // flow) getCurrentUser() is null, and gating the clears on it made
    // logout a no-op that left the challenge user stuck
    sessionStorage.removeItem('idToken');
    sessionStorage.removeItem('accessToken');
    setCurrentUser(null);
    setUserGroups([]);
    setOrganization(null);
    setNewPasswordRequired(false);
    setCurrentCognitoUser(null);
  };
  
  // Context value
  const value = {
    currentUser,
    userGroups,
    organization,
    login,
    logout,
    authError,
    // The temp challenge user has no tokens in sessionStorage — treating it
    // as authenticated let PrivateRoute render protected pages whose API
    // calls all fail. LoginPage keys the challenge form off
    // newPasswordRequired, so this stays true through that flow.
    isAuthenticated: !!currentUser && !currentUser.needsNewPassword,
    isAdmin,
    hasAccess,
    loading,
    // NEW: Expose new password required state and function
    newPasswordRequired,
    completeNewPassword,
    // NEW: Expose forgot password state and functions
    forgotPasswordMode,
    forgotPasswordError,
    initiateForgotPassword,
    confirmForgotPassword,
    cancelForgotPassword
  };
  
  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}